import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, LogIn, ArrowRight, UserPlus } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalLoginPage = () => {
  const { login, register, customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const initialMode = ['signup', 'setup'].includes(searchParams.get('mode')) ? searchParams.get('mode') : 'login';

  useEffect(() => {
    if (customer) {
      if (customer.must_change_password) navigate('/portal/set-password');
      else navigate(redirectTo || '/portal/dashboard');
    }
  }, [customer, navigate, redirectTo]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(initialMode); // login | signup | forgot
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [signupForm, setSignupForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', confirm: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.must_change_password) {
        navigate('/portal/set-password');
      } else {
        navigate(redirectTo || '/portal/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (signupForm.password !== signupForm.confirm) {
      setError('Passwords do not match'); return;
    }
    if (signupForm.password.length < 8) {
      setError('Password must be at least 8 characters'); return;
    }
    setLoading(true);
    try {
      const result = await register({
        first_name: signupForm.first_name.trim(),
        last_name: signupForm.last_name.trim(),
        email: signupForm.email.trim().toLowerCase(),
        phone: signupForm.phone.trim() || null,
        password: signupForm.password,
      });
      if (result.setup_required) {
        setForgotEmail(signupForm.email.trim());
        setForgotSent(true);
        setMode('setup');
      } else {
        navigate(redirectTo || '/portal/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/portal/${mode === 'setup' ? 'request-access' : 'forgot-password'}`, { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch {
      setError('We couldn’t request your link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1B3A5C] to-[#0f2640] flex flex-col" data-testid="portal-login-page">
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          {/* Logo / Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-extrabold text-white mb-2">Client Portal</h1>
            <p className="text-white/50 text-sm">Access your orders, profile, and more</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {['login', 'signup'].includes(mode) && (
              <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6" data-testid="auth-mode-toggle">
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${mode === 'login' ? 'bg-white text-[#1B3A5C] shadow' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="tab-signin"
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('signup'); setError(''); }}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${mode === 'signup' ? 'bg-white text-[#1B3A5C] shadow' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="tab-signup"
                >
                  Sign Up
                </button>
              </div>
            )}

            {mode === 'login' ? (
              <>
                <h2 className="text-xl font-bold text-[#1B3A5C] mb-6" data-testid="login-heading">Sign In</h2>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}
                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="you@email.com" required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                    <div className="relative">
                      <input
                        type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                        placeholder="Enter your password" required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900 pr-12"
                        data-testid="login-password"
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full py-3.5 bg-[#F5A623] text-white font-bold rounded-xl hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="login-submit"
                  >
                    {loading ? 'Signing in...' : <><LogIn className="w-4 h-4" /> Sign In</>}
                  </button>
                </form>
                <button onClick={() => { setMode('setup'); setForgotEmail(email); setForgotSent(false); setError(''); }}
                  className="mt-4 min-h-[44px] text-sm text-[#1B3A5C] underline w-full" data-testid="first-visit-link">
                  First visit? Set up your password
                </button>
                <button onClick={() => { setMode('forgot'); setForgotEmail(email); setForgotSent(false); setError(''); }}
                  className="mt-4 text-sm text-[#4A90A4] hover:underline w-full text-center block"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </button>
              </>
            ) : mode === 'signup' ? (
              <>
                <h2 className="text-xl font-bold text-[#1B3A5C] mb-2" data-testid="signup-heading">Create Your Account</h2>
                <p className="text-gray-500 text-sm mb-6">Track orders, save designs, and reorder in seconds.</p>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="signup-error">
                    {error}
                  </div>
                )}
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                      <input type="text" required value={signupForm.first_name}
                        onChange={e => setSignupForm(p => ({ ...p, first_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                        data-testid="signup-first-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                      <input type="text" required value={signupForm.last_name}
                        onChange={e => setSignupForm(p => ({ ...p, last_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                        data-testid="signup-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input type="email" required value={signupForm.email}
                      onChange={e => setSignupForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="you@email.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                      data-testid="signup-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                    <input type="tel" value={signupForm.phone}
                      onChange={e => setSignupForm(p => ({ ...p, phone: e.target.value }))}
                      placeholder="(443) 940-6660"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                      data-testid="signup-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                    <div className="relative">
                      <input type={showPass ? 'text' : 'password'} required value={signupForm.password}
                        onChange={e => setSignupForm(p => ({ ...p, password: e.target.value }))}
                        placeholder="At least 8 characters"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900 pr-12"
                        data-testid="signup-password"
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                    <input type={showPass ? 'text' : 'password'} required value={signupForm.confirm}
                      onChange={e => setSignupForm(p => ({ ...p, confirm: e.target.value }))}
                      placeholder="Re-enter password"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                      data-testid="signup-confirm"
                    />
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full py-3.5 bg-[#F5A623] text-white font-bold rounded-xl hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="signup-submit"
                  >
                    {loading ? 'Creating account...' : <><UserPlus className="w-4 h-4" /> Create Account <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <p className="text-xs text-gray-400 text-center">
                    By creating an account, you agree to our terms and privacy policy.
                  </p>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-[#1B3A5C] mb-2">{mode === 'setup' ? 'Set Up Portal Access' : 'Reset Password'}</h2>
                <p className="text-gray-500 text-sm mb-6">{mode === 'setup' ? 'Use the email associated with your order. We’ll send a secure link so you can choose your password.' : 'Enter your email to request a password reset link.'}</p>
                {error && <p role="alert" className="mb-4 text-sm text-red-700">{error}</p>}
                {forgotSent ? (
                  <div className="text-center">
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-4 rounded-lg mb-4 text-sm" data-testid="forgot-success">
                      If an account exists with that email, an access link will be sent. Check your inbox and spam folder, or use a recent link. Links can take a few minutes to arrive.
                    </div>
                    <button onClick={() => { setMode('login'); setForgotSent(false); }}
                      className="text-[#4A90A4] hover:underline text-sm">
                      Back to Sign In
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgot} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <input
                        type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                        placeholder="you@email.com" required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                        data-testid="forgot-email"
                      />
                    </div>
                    <button type="submit" disabled={loading}
                      className="w-full py-3.5 bg-[#4A90A4] text-white font-bold rounded-xl hover:brightness-110 transition-all disabled:opacity-50">
                      {loading ? 'Sending...' : mode === 'setup' ? 'Send Setup Link' : 'Send Reset Link'}
                    </button>
                    <button type="button" onClick={() => setMode('login')}
                      className="text-sm text-gray-500 hover:text-[#4A90A4] w-full text-center block">
                      Back to Sign In
                    </button>
                  </form>
                )}
              </>
            )}
          </div>

          <p className="text-center text-white/40 text-xs mt-8">
            Need help? Call us at <a href="tel:443-940-6660" className="text-[#F5A623]">443.940.6660</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PortalLoginPage;
